import { API_TYPES } from "@src/appV2/api/constants";
import { z } from "zod";

import { hcpDocumentAttributesSchema } from "../../../../../Accounts/Documents/types";
import { ESignWorkflowSessionStatus } from "./constants";

const esignProviderDataSchema = z.object({
  provider: z.string(),
  sessionId: z.string().optional(),
});

export const quizResultSchema = z.object({
  needsManualReview: z.boolean(),
  canDisplayAnswers: z.boolean(),
  overallResult: z.enum(["PASSED", "FAILED", "NEEDS_MANUAL_REVIEW"]),
  numberOfQuestions: z.number(),
  numberOfCorrectAnswers: z.number(),
  numberOfIncorrectAnswers: z.number(),
  maxMistakesAllowed: z.number(),
  result: z.array(
    z.object({
      sectionName: z.string().optional(),
      questions: z.array(
        z.object({
          questionNumber: z.number(),
          isCorrect: z.boolean(),
          selectedValues: z.array(z.string()),
          correctValues: z.array(z.string()).optional(),
        })
      ),
    })
  ),
});

export type QuizResult = z.infer<typeof quizResultSchema>;

export const eSignWorkflowSessionAttributesSchema = z.object({
  signRequirementId: z.string(),
  quizResult: quizResultSchema.optional(),
  compositeParentRequirementId: z.string().optional(),
  status: z.nativeEnum(ESignWorkflowSessionStatus),
  esignProviderData: esignProviderDataSchema.optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type ESignWorkflowSessionAttributes = z.infer<typeof eSignWorkflowSessionAttributesSchema>;

const eSignWorkflowSessionRelationshipsSchema = z.object({
  document: z
    .object({
      data: z.object({
        id: z.string(),
        type: z.literal(API_TYPES.documents),
      }),
    })
    .optional(),
});

const eSignWorkflowSessionIncludedSchema = z.array(
  z.object({
    type: z.literal(API_TYPES.documents),
    id: z.string(),
    attributes: hcpDocumentAttributesSchema,
  })
);

const eSignWorkflowSessionDataSchema = z.object({
  type: z.literal(API_TYPES.eSignWorkflowSession),
  id: z.string(),
  attributes: eSignWorkflowSessionAttributesSchema,
  relationships: eSignWorkflowSessionRelationshipsSchema.optional(),
});

export const eSignWorkflowSessionResponseSchema = z.object({
  data: eSignWorkflowSessionDataSchema,
  included: eSignWorkflowSessionIncludedSchema.optional(),
});

export type ESignWorkflowSessionResponse = z.infer<typeof eSignWorkflowSessionResponseSchema>;

export const eSignWorkflowSessionListResponseSchema = z.object({
  data: z.array(eSignWorkflowSessionDataSchema),
  included: eSignWorkflowSessionIncludedSchema.optional(),
});

export type ESignWorkflowSessionListResponse = z.infer<
  typeof eSignWorkflowSessionListResponseSchema
>;
